/**
 * components/import/statements/DuplicateWarningDialog.jsx — Phase 84.3 slice-1
 *
 * "This statement looks familiar" dialog, shown when a file's SHA-256
 * matches a prior parse_job for this user. The user can always proceed;
 * we never hard-block. Extracted verbatim from pages/CSVImport.js
 * (was internal function `DuplicateWarningDialog`). Zero behavior change.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { FileType2 } from 'lucide-react';

export default function DuplicateWarningDialog({ dupDialog, setDupDialog, proceed, accounts }) {
  if (!dupDialog) return null;
  const { match, file } = dupDialog;
  const matchAccount = (accounts || []).find((a) => a.id === match.account_id);
  const created = match.created_at ? new Date(match.created_at) : null;
  const dateLabel = created
    ? created.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : 'earlier';
  const statusLabel = match.consumed_at
    ? 'imported into your transactions'
    : match.status === 'done'
      ? 'parsed but not imported'
      : match.status;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) setDupDialog(null); }}>
      <DialogContent className="rounded-2xl sm:max-w-lg" data-testid="duplicate-warning-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">This statement looks familiar</DialogTitle>
          <DialogDescription>
            We&rsquo;ve seen a file with identical contents before. Importing it again may create duplicate transactions.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-border/70 bg-muted/40 p-3.5 space-y-1.5 text-xs overflow-hidden">
          <div className="flex items-center gap-2 min-w-0">
            <FileType2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="font-medium truncate min-w-0" title={match.filename || file?.name || ''}>{match.filename || file?.name || 'previous upload'}</span>
          </div>
          <div className="text-muted-foreground">
            Uploaded <span className="text-foreground">{dateLabel}</span>
            {matchAccount && <> &middot; to <span className="text-foreground">{matchAccount.name}{matchAccount.last4 ? ` \u2022\u2022${matchAccount.last4}` : ''}</span></>}
          </div>
          <div className="text-muted-foreground">
            Status: <span className="text-foreground">{statusLabel}</span>
            {match.txn_count ? <> &middot; <span className="text-foreground">{match.txn_count} transactions</span></> : null}
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => setDupDialog(null)} className="rounded-xl" data-testid="duplicate-cancel">
            Cancel
          </Button>
          <Button onClick={proceed} className="rounded-xl" data-testid="duplicate-proceed">
            Import anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
